import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { db } from "../lib/firebase";
import { Play, Search } from "lucide-react";

const CATEGORIES = ["All", "Music", "DJ Sets", "Film", "Business", "Gaming", "Previously Recorded"];

export default function Explore() {
  const [active, setActive] = useState("All");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => { loadContent(); }, [active]);

  const loadContent = async () => {
    setLoading(true);
    try {
      let q;
      if (active === "All") {
        q = query(collection(db, "content"), where("status", "==", "approved"), orderBy("createdAt", "desc"));
      } else {
        q = query(collection(db, "content"), where("status", "==", "approved"), where("category", "==", active), orderBy("createdAt", "desc"));
      }
      const snap = await getDocs(q);
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error(e);
      setItems([]);
    }
    setLoading(false);
  };

  const filtered = search
    ? items.filter((i) => i.title?.toLowerCase().includes(search.toLowerCase()) || i.creatorName?.toLowerCase().includes(search.toLowerCase()))
    : items;

  return (
    <div data-testid="explore-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <h1 className="text-2xl font-black text-white">Explore</h1>
        <div className="flex items-center gap-2 bg-[#111] border border-white/10 rounded-xl px-3 py-2 flex-1 sm:max-w-xs">
          <Search size={14} className="text-zinc-500" />
          <input
            data-testid="explore-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search content, creators..."
            className="bg-transparent text-white text-sm flex-1 focus:outline-none placeholder-zinc-600"
          />
        </div>
      </div>

      {/* Category filters */}
      <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            data-testid={`category-${cat.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setActive(cat)}
            className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
              active === cat
                ? "bg-[#00ff77] text-black font-bold"
                : "bg-[#111] border border-white/10 text-zinc-400 hover:border-[#00ff77]/30 hover:text-white"
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-[#111] rounded-2xl overflow-hidden animate-pulse">
              <div className="aspect-video bg-[#1a1a1a]" />
              <div className="p-3 space-y-2">
                <div className="h-4 bg-[#1a1a1a] rounded w-3/4" />
                <div className="h-3 bg-[#1a1a1a] rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((item) => (
            <Link
              key={item.id}
              to={`/content/${item.id}`}
              data-testid={`explore-card-${item.id}`}
              className="group bg-[#111] border border-white/5 rounded-2xl overflow-hidden hover:bg-[#1a1a1a] hover:border-[#00ff77]/30 hover:shadow-[0_0_20px_rgba(0,255,119,0.1)] transition-all duration-300"
            >
              <div className="relative aspect-video bg-[#1a1a1a]">
                {item.thumbnailURL ? (
                  <img src={item.thumbnailURL} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Play size={28} className="text-zinc-600" />
                  </div>
                )}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-10 h-10 rounded-full bg-[#00ff77] flex items-center justify-center">
                    <Play size={14} className="text-black ml-0.5" fill="black" />
                  </div>
                </div>
              </div>
              <div className="p-3">
                <h3 className="text-white text-sm font-semibold line-clamp-2 mb-1">{item.title}</h3>
                <p className="text-zinc-500 text-xs">{item.creatorName}</p>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-zinc-500">
          <p className="text-sm">No content found{active !== "All" ? ` in ${active}` : ""}.</p>
        </div>
      )}
    </div>
  );
}
